import type { AxiosResponse } from 'axios';

export interface BlockingPackage {
  tracking: string;
  status: number;
}

export class MassiveApiError extends Error {
  status: number;

  unreachable: boolean;

  constructor(status: number, message: string, unreachable = false) {
    super(message);
    this.name = 'MassiveApiError';
    this.status = status;
    this.unreachable = unreachable;
  }
}

export class MassiveConflictError extends MassiveApiError {
  blocking: BlockingPackage[];

  constructor(blocking: BlockingPackage[]) {
    super(409, 'La masiva tiene paquetes que ya se empezaron a operar');
    this.name = 'MassiveConflictError';
    this.blocking = blocking;
  }
}

const UNREACHABLE_STATUSES = [0, 500, 502, 503, 504];

export const MASSIVE_UNREACHABLE =
  'El servicio de altas masivas no está respondiendo. Volvé a intentar en unos minutos; si sigue igual, avisá a soporte.';

const messageFor = (
  status: number,
  body?: { error?: string },
  unreachable = MASSIVE_UNREACHABLE,
): string => {
  if (UNREACHABLE_STATUSES.includes(status)) {
    return unreachable;
  }
  return body?.error ?? `La petición falló con estado ${status}.`;
};

export function assertOk<T>(
  response: AxiosResponse<T>,
  unreachable = MASSIVE_UNREACHABLE,
): T {
  const { status, data } = response;
  if (status >= 200 && status < 300) {
    return data;
  }
  throw new MassiveApiError(
    status,
    messageFor(status, data as { error?: string }, unreachable),
    UNREACHABLE_STATUSES.includes(status),
  );
}

export const toMassiveError = (
  error: unknown,
  unreachable = MASSIVE_UNREACHABLE,
): MassiveApiError => {
  if (error instanceof MassiveApiError) {
    return error;
  }
  const status =
    (error as { response?: { status?: number } }).response?.status ?? 0;
  const body = (error as { response?: { data?: { error?: string } } }).response
    ?.data;
  return new MassiveApiError(
    status,
    messageFor(status, body, unreachable),
    UNREACHABLE_STATUSES.includes(status),
  );
};
